// The app header: the design system's two-row Topbar (white, a hairline under it).
//   Tool row: project switcher · search · Ask · Feedback · Help · Alerts · organisation · avatar
//   Row 2: wordmark · Home · Activities · Experts · Stakeholders ·
//          Administration & Reporting · Analytics
// On People and organisation (/org...) row 2 shows that section's areas
// instead: Organisation · {the organisation's word} · Insight · My tasks, and
// a Settings gear at the end; the project chooser stays in row 1.
// Below 768px: a Search link replaces the box, the organisation menu moves
// into the avatar menu, and the nav collapses behind a "Menu" button.
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import type { Me } from "@convenor/shared";
import { api } from "../api/client";
import { useMeta } from "../api/meta";
import { lastProject, useProjectList, type ProjectListItem } from "../api/project";
import { useLogout, useSwitchOrg } from "../api/session";
import { Menu } from "../ui/Menu";
import { AssistantPanel } from "../areas/assistant/AssistantPanel";
import { FeedbackButton } from "../areas/feedback-button";
import { orgNavItems, useOrgSection, type OrgNavKey } from "../areas/workforce/components/OrgLayout";
import { useOrgWords } from "../areas/workforce/queries";
import { hasOrgArea } from "./OrgAreaGate";
import { Wordmark } from "./Wordmark";

export type NavSection = "home" | "activities" | "experts" | "stakeholders" | "admin" | "analytics" | null;

export const NAV_ITEMS: { key: Exclude<NavSection, null>; label: string; path: string }[] = [
  { key: "home", label: "Home", path: "" },
  { key: "activities", label: "Activities", path: "/activities" },
  { key: "experts", label: "Experts", path: "/experts" },
  { key: "stakeholders", label: "Stakeholders", path: "/stakeholders" },
  { key: "admin", label: "Administration & Reporting", path: "/admin" },
  { key: "analytics", label: "Analytics", path: "/analytics" },
];

/** Which main-nav item is current for a path under /p/:projectId. */
export function navSection(pathname: string): NavSection {
  const m = /^\/p\/[^/]+(\/([^/]+))?/.exec(pathname);
  if (!m) return null;
  const first = m[2] ?? "";
  if (first === "") return "home";
  if (first === "activities") return "activities";
  if (first === "experts") return "experts";
  if (first === "stakeholders") return "stakeholders";
  if (first === "admin") return "admin";
  if (first === "analytics") return "analytics";
  return null;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");

/** The project the header shows: the URL's, else the last one opened. */
function useHeaderProject(projectId: string | null, projects: ProjectListItem[] | undefined) {
  const id = projectId ?? null;
  return projects?.find((p) => p.id === id) ?? null;
}

export function AppHeader({ me, projectId }: { me: Me; projectId: string | null }) {
  const meta = useMeta();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  /** The support assistant (docs/v3/ASSISTANT.md 1). */
  const [askOpen, setAskOpen] = useState(false);
  const projects = useProjectList();
  const current = useHeaderProject(projectId, projects.data);
  const homeProject = projectId ?? lastProject() ?? projects.data?.[0]?.id ?? null;
  const section = navSection(location.pathname);
  const orgSectionKey = useOrgSection(location.pathname);
  // People and organisation only when the organisation has it switched on (OrgAreaGate).
  const org = hasOrgArea(me) ? orgSectionKey : null;
  const unread = useQuery({
    queryKey: ["notifications-count"],
    queryFn: () => api.get<{ unread: number }>("/api/notifications/count"),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const count = unread.data?.unread ?? 0;

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const onSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = new FormData(e.currentTarget).get("q")?.toString().trim() ?? "";
    if (homeProject) navigate(`/p/${homeProject}/search?q=${encodeURIComponent(q)}`);
  };

  const showSwitcher = Boolean(projectId) || !/^\/(projects|settings|notifications|help)/.test(location.pathname);
  const base = homeProject ? `/p/${homeProject}` : null;

  return (
    <header className={menuOpen ? "topbar topbar--two-row topbar--open" : "topbar topbar--two-row"}>
      <div className="topbar__toolrow">
        {showSwitcher ? <ProjectSwitcher current={current} projects={projects.data ?? []} /> : null}
        {base ? (
          <>
            <form role="search" onSubmit={onSearch}>
              <label htmlFor="global-search" className="visually-hidden">
                Search the project
              </label>
              <input id="global-search" name="q" type="search" placeholder="Search the project" className="topbar__search" />
            </form>
            <Link className="btn btn--quiet topbar__search-link" to={`${base}/search`}>
              Search
            </Link>
          </>
        ) : null}
        {meta.demoMode ? null : (
          <button type="button" className="btn btn--quiet" aria-expanded={askOpen} onClick={() => setAskOpen((v) => !v)}>
            Ask
          </button>
        )}
        {meta.demoMode ? null : <FeedbackButton projectId={projectId} />}
        <Link
          className={location.pathname === "/help" ? "btn btn--secondary" : "btn btn--quiet"}
          to="/help"
          state={{ from: `${location.pathname}${location.search}` }}
          aria-current={location.pathname === "/help" ? "page" : undefined}
        >
          Help
        </Link>
        <Link className="btn btn--quiet" to="/notifications" aria-label={`Notifications, ${count} new`}>
          <span aria-hidden="true">Alerts</span>
          {count > 0 ? (
            <span className="topbar__count" aria-hidden="true">
              {count}
            </span>
          ) : null}
        </Link>
        {/* The demo: the organisation's name only, no menu (nothing there works in a read-only demo). */}
        {meta.demoMode ? me.org ? <span className="topbar__org">{me.org.name}</span> : null : <OrgMenu me={me} projectBase={base} />}
        <AvatarMenu me={me} projectBase={base} demo={meta.demoMode} />
      </div>
      <Wordmark to={base ?? "/projects"} />
      <button type="button" className="btn btn--secondary topbar__menu-btn" aria-expanded={menuOpen} aria-controls="main-nav" onClick={() => setMenuOpen((v) => !v)}>
        Menu
      </button>
      <nav id="main-nav" aria-label={org ? "People and organisation" : "Main"} className="topbar__nav">
        {org ? (
          <OrgNavList current={org} />
        ) : (
          NAV_ITEMS.map((item) =>
            base ? (
              <Link key={item.key} className="topbar__link" to={`${base}${item.path}`} aria-current={section === item.key ? "page" : undefined}>
                {item.label}
              </Link>
            ) : (
              <Link key={item.key} className="topbar__link" to="/projects">
                {item.label}
              </Link>
            ),
          )
        )}
      </nav>
      {askOpen && !meta.demoMode ? <AssistantPanel audience="team" onClose={() => setAskOpen(false)} /> : null}
    </header>
  );
}

/**
 * The People and organisation areas. The second item is the organisation's
 * own word (Samenwerkingsverbanden): until the settings (or this tab's last
 * known words) are there, it keeps its place but stays invisible, so the
 * header never flashes the default word.
 */
function OrgNavList({ current }: { current: OrgNavKey }) {
  const words = useOrgWords();
  const items = orgNavItems(words.collabLabelPlural);
  return (
    <>
      {items.map((item) =>
        item.key === "settings" ? (
          <Link key={item.key} className="topbar__link" to={item.to} aria-label={item.label} aria-current={current === item.key ? "page" : undefined}>
            <GearIcon />
            <span className="visually-hidden">{item.label}</span>
          </Link>
        ) : (
          <Link
            key={item.key}
            className="topbar__link"
            to={item.to}
            aria-current={current === item.key ? "page" : undefined}
            aria-busy={item.key === "collabs" && !words.ready ? true : undefined}
          >
            {item.key === "collabs" && !words.ready ? <span className="visually-hidden">{item.label}</span> : item.label}
          </Link>
        ),
      )}
    </>
  );
}

function GearIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M19.14 12.94a7.07 7.07 0 0 0 0-1.88l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.03 7.03 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 2.5h-3.84a.5.5 0 0 0-.49.42l-.36 2.54c-.59.24-1.14.55-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.67 8.98a.5.5 0 0 0 .12.64l2.03 1.58a7.07 7.07 0 0 0 0 1.88l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32c.13.22.39.3.6.22l2.39-.96c.49.39 1.04.7 1.63.94l.36 2.54c.04.24.25.42.49.42h3.84c.24 0 .45-.18.49-.42l.36-2.54c.59-.24 1.14-.55 1.63-.94l2.39.96c.22.08.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Z"
      />
    </svg>
  );
}

function ProjectSwitcher({ current, projects }: { current: ProjectListItem | null; projects: ProjectListItem[] }) {
  const others = projects.filter((p) => p.id !== current?.id && p.status !== "closed");
  return (
    <Menu
      align="left"
      className="project-switcher"
      label={
        <>
          <span className="project-switcher__label">Project</span>{" "}
          <b className="project-switcher__name">{current ? `${current.name} · ${current.reference}` : "Choose a project"}</b>
        </>
      }
    >
      {current ? (
        <li>
          <Link to={`/p/${current.id}`} aria-current="page">
            {current.name} · {current.reference}
          </Link>
        </li>
      ) : null}
      {others.slice(0, 8).map((p) => (
        <li key={p.id}>
          <Link to={`/p/${p.id}`}>
            {p.name} · {p.reference}
          </Link>
        </li>
      ))}
      <li className="menu__separator" role="separator" />
      <li>
        <Link to="/projects">All projects</Link>
      </li>
    </Menu>
  );
}

function OrgMenu({ me, projectBase, inline = false }: { me: Me; projectBase: string | null; inline?: boolean }) {
  const switchOrg = useSwitchOrg();
  const navigate = useNavigate();
  if (!me.org) return null;
  const others = me.orgs.filter((o) => o.id !== me.org?.id);
  const items = (
    <>
      <li className="menu__heading">{me.org.name}</li>
      <li>
        <Link to="/settings/team">Organisation settings</Link>
      </li>
      {hasOrgArea(me) ? (
        <li>
          <Link to="/org">People and organisation</Link>
        </li>
      ) : null}
      {projectBase ? (
        <li>
          <Link to={`${projectBase}/settings/partners`}>Project partners</Link>
        </li>
      ) : null}
      {others.map((o) => (
        <li key={o.id}>
          <button type="button" onClick={() => switchOrg.mutate({ orgId: o.id }, { onSuccess: () => navigate("/projects") })}>
            Switch to {o.name}
          </button>
        </li>
      ))}
      <li>
        <Link to="/welcome/organisation">Create an organisation</Link>
      </li>
    </>
  );
  if (inline) return items;
  return (
    <Menu
      className="org-menu"
      label={
        <>
          <span className="visually-hidden">Organisation: </span>
          <span className="org-menu__name">{me.org.name}</span>
        </>
      }
    >
      {items}
    </Menu>
  );
}

function AvatarMenu({ me, projectBase, demo }: { me: Me; projectBase: string | null; demo: boolean }) {
  const logout = useLogout();
  const navigate = useNavigate();
  return (
    <Menu
      className="avatar-menu"
      label={
        <>
          <span className="avatar avatar--s avatar--me" aria-hidden="true">
            {initials(me.user.name) || "?"}
          </span>
          <span className="visually-hidden">My account</span>
        </>
      }
    >
      <li className="menu__heading">{me.user.name}</li>
      <li>
        <Link to="/settings/account">My account</Link>
      </li>
      {/* GET /api/me is the v3 Me; the v1 type does not know the staff flag yet. */}
      {(me.user as { platformAdmin?: boolean }).platformAdmin ? (
        <>
          <li>
            <Link to="/staff/emails">Email log</Link>
          </li>
          <li>
            <Link to="/staff/trials">Trials</Link>
          </li>
        </>
      ) : null}
      {demo ? null : (
        <li className="avatar-menu__org">
          <ul className="avatar-menu__org-list">
            <OrgMenu me={me} projectBase={projectBase} inline />
          </ul>
        </li>
      )}
      <li>
        <button type="button" onClick={() => logout.mutate(undefined, { onSettled: () => navigate("/sign-in") })}>
          Sign out
        </button>
      </li>
    </Menu>
  );
}
